import { Box, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { GridProgress } from '../../common/components'

import CustomPagination from '@/common/components/CustomPagination/CustomPagination'
import { usePagination } from '@/common/components/CustomPagination/hooks/usePagination'
import { useTenantsPrefetch as usePrefetch } from '@/services/api/tenant/tenantsEnhanced'

import { DataListItem } from './DataListItem/DataListItem'
import { TableNoDataRow } from './TableNoDataRow/TableNoDataRow'

export type DataListProps<T> = {
  titles: string[]
  emptyList: string
  useSearchList: (
    page: number,
    size: number,
  ) => {
    data: { items: T[]; total: number } | undefined
    isFetching: boolean
    isUninitialized: boolean
  }
}

export const DataList = <T extends { id: string; code?: string; name?: string }>({
  titles,
  emptyList,
  useSearchList,
}: DataListProps<T>): JSX.Element => {
  const { t, i18n } = useTranslation('general')
  const { page, size: limit, handlePageChange, handleLimitChange } = usePagination()
  const { data: list = { items: [], total: 0 }, isFetching, isUninitialized } = useSearchList(page, limit)
  const isLoading = isFetching || isUninitialized

  const prefetchPage = usePrefetch('listTenants')

  const prefetchNext = useCallback(() => {
    prefetchPage({ page: page + 1, size: limit })
  }, [prefetchPage, page, limit])

  return useMemo(
    () => (
      <GridProgress loading={isLoading} key={i18n.language}>
        <Box sx={{ minWidth: 1050, mt: 3 }}>
          <TableContainer sx={{ borderRadius: '10px' }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8f8fa', borderBottom: '0px solid' }}>
                <TableRow>
                  {titles.map((title) => (
                    <TableCell key={title}>{title}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {list.items.length === 0 ? (
                  <TableNoDataRow colSpan={titles.length} message={emptyList} />
                ) : (
                  list.items.map((object) => (
                    <TableRow hover key={object.id}>
                      <TableCell>
                        <DataListItem
                          data={object}
                          objectType={object.code ? `${object.code}` : `${object.id}`}
                          nameItem={object.code ? `${object.code} - ${object.name}` : `${object.name}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            mt: 2,
            pl: 1,
          }}>
          {(!list.total || list.total <= 25) && (
            <Typography sx={{ fontSize: '14px', mr: 2 }}>
              {t('item1')} {list.total} {t('item2')}
            </Typography>
          )}

          <CustomPagination
            total={list.total}
            limit={limit}
            page={page}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            handlePrefetch={prefetchNext}
          />
        </Stack>
      </GridProgress>
    ),
    [isLoading, list, page, limit, i18n.language, prefetchNext, handlePageChange, handleLimitChange],
  )
}
